<template>
  <div class="category-page">
    <div class="container">
      <!-- Category Header -->
      <div class="category-header-wrapper">
        <div class="category-header">
          <h1>{{ category.name }}</h1>
          <p v-if="category.description" class="category-description">{{ category.description }}</p>
        </div>
      </div>

      <!-- Subcategories -->
      <div v-if="subCategories && subCategories.length > 0" class="subcategories-wrapper">
        <div class="subcategories">
          <div class="subcategory-scroll">
          <button class="scroll-btn scroll-left" @click="scrollLeft">
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="subcategory-grid" ref="subcategoryContainer">
            <a
              v-for="sub in subCategories"
              :key="sub.id"
              :href="`/${category.slug}/${sub.slug}`"
              class="subcategory-item"
            >
              <div class="subcategory-image">
                <img :src="sub.image || '/images/placeholder.jpg'" :alt="sub.name" />
              </div>
              <span class="subcategory-name">{{ sub.name }}</span>
            </a>
          </div>
          <button class="scroll-btn scroll-right" @click="scrollRight">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
        </div>
      </div>

      <!-- Products Section -->
      <div class="products-wrapper">
        <div class="products-section">
          <div class="page-header">
            <p class="product-count">{{ products.total || 0 }} products</p>
            <div class="sort-control">
              <label for="sort" class="sort-label">Sort:</label>
              <select id="sort" class="sort-select" v-model="selectedSort" @change="applySort">
                <option value="newest">Newest</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="name_asc">Name: A-Z</option>
                <option value="name_desc">Name: Z-A</option>
              </select>
            </div>
          </div>

        <!-- Products Grid -->
        <div v-if="products.data && products.data.length > 0" class="products-grid">
          <product-card
            v-for="product in products.data"
            :key="product.id"
            :product="formatProduct(product)"
          />
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <i class="fas fa-box-open"></i>
          <h3>No Products Available</h3>
          <p>Check back later for new products in this category</p>
        </div>

        <!-- Pagination -->
        <div v-if="products.data && products.data.length > 0 && products.last_page > 1" class="pagination">
          <a
            v-if="products.prev_page_url"
            :href="products.prev_page_url"
            class="page-btn"
          >
            <i class="fas fa-chevron-left"></i> Previous
          </a>

          <span class="page-info">
            Page {{ products.current_page }} of {{ products.last_page }}
          </span>

          <a
            v-if="products.next_page_url"
            :href="products.next_page_url"
            class="page-btn"
          >
            Next <i class="fas fa-chevron-right"></i>
          </a>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import ProductCard from '../../components/home/ProductCard.vue'

const props = defineProps({
  category: {
    type: Object,
    required: true
  },
  subCategories: {
    type: Array,
    default: () => []
  },
  products: {
    type: Object,
    required: true
  },
  sort: {
    type: String,
    default: 'newest'
  }
})

const selectedSort = ref(props.sort || 'newest')
const subcategoryContainer = ref(null)

const scrollLeft = () => {
  if (subcategoryContainer.value) {
    subcategoryContainer.value.scrollBy({ left: -300, behavior: 'smooth' })
  }
}

const scrollRight = () => {
  if (subcategoryContainer.value) {
    subcategoryContainer.value.scrollBy({ left: 300, behavior: 'smooth' })
  }
}

const applySort = () => {
  const url = new URL(window.location.href)
  url.searchParams.set('sort', selectedSort.value)
  url.searchParams.delete('page')
  window.location.href = url.toString()
}

const formatProduct = (product) => {
  // Consistent with Product model/migration: base_price = final price, compare_price = old crossed-out price
  const sellingPrice = product.base_price
  const hasDiscount = product.compare_price && product.compare_price > product.base_price
  const discount = hasDiscount
    ? Math.round(((product.compare_price - product.base_price) / product.compare_price) * 100)
    : null

  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    price: sellingPrice,
    original_price: hasDiscount ? product.compare_price : null,
    image: product.image || '/images/placeholder.jpg',
    discount: discount,
    rating: product.average_rating || 0,
    reviews: product.reviews_count || 0,
    stock_status: product.is_active ? 'in-stock' : 'out'
  }
}
</script>
